import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Smartphone, Zap, Wifi, Bell } from 'lucide-react';
import { usePWA } from '../../hooks/usePWA';
import toast from 'react-hot-toast';

const PWAInstallPrompt: React.FC = () => {
  const { capabilities, installApp } = usePWA();
  const [showPrompt, setShowPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Show install prompt if app is installable and not dismissed
    const isDismissed = localStorage.getItem('pwa-install-dismissed') === 'true';
    
    if (capabilities.isInstallable && !capabilities.isInstalled && !isDismissed) {
      // Delay showing the prompt to avoid interrupting user flow
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [capabilities.isInstallable, capabilities.isInstalled]);

  const handleInstall = async () => {
    const success = await installApp();
    
    if (success) {
      toast.success('DisasterConnect installed successfully!');
      setShowPrompt(false);
    } else {
      toast.error('Installation cancelled or failed');
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  const handleRemindLater = () => {
    setShowPrompt(false);
    // Set reminder for 24 hours
    const reminderTime = Date.now() + (24 * 60 * 60 * 1000);
    localStorage.setItem('pwa-install-reminder', reminderTime.toString());
  };

  if (!showPrompt || capabilities.isInstalled || !capabilities.isInstallable) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50"
      >
        <div className="bg-dark-800 border border-dark-600 rounded-xl p-6 shadow-2xl backdrop-blur-sm">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-primary-400 to-primary-500 rounded-lg flex items-center justify-center">
                <Download className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Install DisasterConnect</h3>
                <p className="text-sm text-gray-400">Get the full app experience</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>

          {/* Features */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="flex items-center space-x-2 text-sm text-gray-300">
              <Smartphone className="w-4 h-4 text-primary-400" />
              <span>Native app feel</span>
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-300">
              <Wifi className="w-4 h-4 text-accent-green" />
              <span>Works offline</span>
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-300">
              <Bell className="w-4 h-4 text-accent-orange" />
              <span>Push notifications</span>
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-300">
              <Zap className="w-4 h-4 text-accent-yellow" />
              <span>Faster loading</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleInstall}
              className="flex-1 bg-gradient-to-r from-primary-400 to-primary-500 py-3 rounded-lg font-semibold text-white hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              Install App
            </motion.button>
            <button
              onClick={handleRemindLater}
              className="px-4 py-3 border border-dark-600 rounded-lg text-gray-300 hover:bg-dark-700 transition-colors text-sm"
            >
              Later
            </button>
          </div>

          {/* Benefits */}
          <div className="mt-4 pt-4 border-t border-dark-700">
            <p className="text-xs text-gray-500 text-center">
              Install for instant access to emergency features, even when offline
            </p>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PWAInstallPrompt;